import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

/**
 * Component that checks for redirects before allowing 404 to show
 * This handles redirects in development mode when React Router handles routes
 */
function RedirectChecker({ children }) {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const checkRedirect = async () => {
      // Skip checking for known routes that should work normally
      const knownRoutePrefixes = [
        '/admin',
        '/design',
        '/checkout',
        '/note',
        '/notes',
        '/api',
        '/static'
      ];
      
      // Skip if it's a known route prefix
      if (knownRoutePrefixes.some(prefix => location.pathname.startsWith(prefix))) {
        setChecking(false);
        return;
      }

      // Skip root and common routes
      if (location.pathname === '/' || location.pathname === '/search' || location.pathname === '/login') {
        setChecking(false);
        return;
      }

      try {
        // Check if this path has a redirect
        // Remove leading slash from path for the API endpoint
        const path = location.pathname;
        const pathWithoutSlash = path.startsWith('/') ? path.substring(1) : path;
        const response = await axios.get(`${API_BASE_URL}/redirect/check/${pathWithoutSlash}`);
        
        if (response.data.success && response.data.exists) {
          const redirect = response.data.data;
          setRedirecting(true);
          
          // If password is required, redirect to server (which will show password form)
          if (redirect.hasPassword) {
            // Get the server URL (remove /api from API_BASE_URL)
            const serverUrl = API_BASE_URL.replace('/api', '');
            window.location.href = `${serverUrl}${path}`;
            return;
          }
          
          // No password required - redirect directly to target URL
          window.location.href = redirect.targetUrl;
          return;
        }
      } catch (error) {
        // If redirect check fails (404, network error, etc.), continue normally
        // This is expected for paths that don't have redirects
        console.debug('No redirect found for path:', location.pathname);
      } finally {
        setChecking(false);
      }
    };

    checkRedirect();
  }, [location.pathname]);

  if (redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return children;
}

export default RedirectChecker;
